import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { syncSessionFromApi } from "../../../slices/session";
import {
  getCompanyFeatures,
  updateCompanyFeatures,
  fetchCompanyAiUsageSummary,
} from "../../../api/company";
import SimplePrompt from "../../../components/utils/simplePrompt";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";

const AI_PRESETS = [
  { value: "this_month", labelKey: "subscription.ai_usage.period.this_month" },
  { value: "this_year", labelKey: "subscription.ai_usage.period.this_year" },
  { value: "last_30_days", labelKey: "subscription.ai_usage.period.last_30_days" },
  { value: "last_12_months", labelKey: "subscription.ai_usage.period.last_12_months" },
];

function featureLabel(t, feature) {
  const slug = String(feature).replace(/\./g, "_");
  const key = `subscription.ai_usage.feature.${slug}`;
  const specific = t(key, { defaultValue: "" });
  if (specific && specific !== key) return specific;
  return t("subscription.ai_usage.feature.other", { feature });
}

function formatMinutesSaved(t, minutes) {
  const m = Number(minutes);
  if (!Number.isFinite(m) || m <= 0) return "—";
  if (m < 60) {
    return t("subscription.ai_usage.minutes_only", { minutes: Math.round(m) });
  }
  const h = Math.floor(m / 60);
  const min = Math.round(m % 60);
  return t("subscription.ai_usage.hours_minutes", { hours: h, minutes: min });
}

const CompanySubscription = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentCompany = useSelector((state) => state.session.currentCompany);
  const [modules, setModules] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [promptOpen, setPromptOpen] = useState(false);
  const [moduleToToggle, setModuleToToggle] = useState(null);

  const [aiPreset, setAiPreset] = useState("this_month");
  const [aiSummary, setAiSummary] = useState(null);
  const [aiLoading, setAiLoading] = useState(true);
  const [aiError, setAiError] = useState(null);

  const loadAiSummary = useCallback(async () => {
    try {
      setAiLoading(true);
      setAiError(null);
      const res = await fetchCompanyAiUsageSummary({ preset: aiPreset });
      if (res.error) {
        if (res.status === 403) {
          setAiSummary(null);
          setAiError("forbidden");
        } else {
          setAiSummary(null);
          setAiError("load");
        }
        return;
      }
      setAiSummary(res.data);
    } catch {
      setAiSummary(null);
      setAiError("load");
    } finally {
      setAiLoading(false);
    }
  }, [aiPreset]);

  useEffect(() => {
    fetchCompanyFeatures();
  }, []);

  useEffect(() => {
    loadAiSummary();
  }, [loadAiSummary]);

  const fetchCompanyFeatures = async () => {
    try {
      setIsLoading(true);
      const response = await getCompanyFeatures();
      if (response.error) {
        setError("Kunne ikke hente moduler");
      } else {
        setModules(response.data || []);
      }
    } catch (err) {
      setError("En feil oppstod ved henting av moduler");
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleClick = (module) => {
    // Activate only when catalog is open; deactivate always (Fjern tilgang) even if closed.
    if (!module.active && module.enabled) {
      setModuleToToggle(module);
      setPromptOpen(true);
    } else if (module.active) {
      handleToggleModule(module);
    }
  };

  const handleConfirmToggle = () => {
    if (moduleToToggle) {
      handleToggleModule(moduleToToggle);
      setPromptOpen(false);
      setModuleToToggle(null);
    }
  };

  const handleToggleModule = async (module) => {
    try {
      setSaving(true);
      setError(null);

      const newActiveStatus = !module.active;

      setModules((prevModules) =>
        prevModules.map((m) =>
          m.name === module.name ? { ...m, active: newActiveStatus } : m
        )
      );

      const response = await updateCompanyFeatures({
        module_name: module.name,
        active: newActiveStatus,
      });

      if (response.error) {
        setModules((prevModules) =>
          prevModules.map((m) =>
            m.name === module.name ? { ...m, active: !newActiveStatus } : m
          )
        );
        setError("Kunne ikke oppdatere modul");
      } else {
        if (response.data) {
          setModules((prevModules) =>
            prevModules.map((m) =>
              m.name === module.name
                ? {
                    ...m,
                    active: response.data.active,
                    feature_id: response.data.id,
                  }
                : m
            )
          );
        }
        await dispatch(syncSessionFromApi());
      }
    } catch (err) {
      setError("En feil oppstod ved oppdatering");
    } finally {
      setSaving(false);
    }
  };

  function AiUsageSection() {
    const rate = aiSummary?.cost_assumptions?.nok_per_million_tokens ?? 30;
    const monthly = aiSummary?.monthly_series || [];
    const maxMonthTokens =
      monthly.length > 0 ? Math.max(...monthly.map((r) => r.total_tokens || 0), 1) : 1;

    return (
      <div className="mb-10 rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="border-b border-slate-100 bg-gradient-to-r from-slate-50 to-indigo-50/40 px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              {t("subscription.ai_usage.title")}
            </h2>
            <p className="text-sm text-slate-600 mt-1 max-w-2xl">
              {t("subscription.ai_usage.subtitle")}
            </p>
          </div>
          <div className="flex-shrink-0">
            <label className="sr-only" htmlFor="ai-usage-preset">
              Period
            </label>
            <select
              id="ai-usage-preset"
              value={aiPreset}
              onChange={(e) => setAiPreset(e.target.value)}
              className="block w-full sm:w-auto rounded-lg border-slate-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500 py-2 pl-3 pr-8"
            >
              {AI_PRESETS.map((p) => (
                <option key={p.value} value={p.value}>
                  {t(p.labelKey)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="p-6">
          {aiLoading && (
            <div className="text-sm text-slate-500 py-8 text-center">
              {t("subscription.ai_usage.loading")}
            </div>
          )}

          {!aiLoading && aiError === "forbidden" && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900">
              {t("subscription.ai_usage.forbidden")}
            </div>
          )}

          {!aiLoading && aiError === "load" && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
              {t("subscription.ai_usage.load_failed")}
            </div>
          )}

          {!aiLoading && !aiError && aiSummary && (
            <>
              {aiSummary.totals?.successful_calls === 0 &&
              (aiSummary.totals?.failed_calls || 0) === 0 ? (
                <p className="text-sm text-slate-600 py-4">{t("subscription.ai_usage.empty")}</p>
              ) : (
                <>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <div className="rounded-lg bg-slate-50 border border-slate-100 p-4">
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                        {t("subscription.ai_usage.tokens")}
                      </p>
                      <p className="text-2xl font-bold text-slate-900 mt-1 tabular-nums">
                        {(aiSummary.totals?.total_tokens ?? 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="rounded-lg bg-slate-50 border border-slate-100 p-4">
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                        {t("subscription.ai_usage.estimated_cost")}
                      </p>
                      <p className="text-2xl font-bold text-slate-900 mt-1 tabular-nums">
                        {new Intl.NumberFormat("nb-NO", {
                          style: "currency",
                          currency: "NOK",
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 2,
                        }).format(aiSummary.estimated_cost_nok ?? 0)}
                      </p>
                    </div>
                    <div className="rounded-lg bg-slate-50 border border-slate-100 p-4">
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                        {t("subscription.ai_usage.calls_ok")}
                      </p>
                      <p className="text-2xl font-bold text-emerald-700 mt-1 tabular-nums">
                        {aiSummary.totals?.successful_calls ?? 0}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        {t("subscription.ai_usage.calls_fail")}:{" "}
                        {aiSummary.totals?.failed_calls ?? 0}
                      </p>
                    </div>
                    <div className="rounded-lg bg-slate-50 border border-slate-100 p-4">
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                        {t("subscription.ai_usage.time_saved")}
                      </p>
                      <p className="text-2xl font-bold text-indigo-800 mt-1">
                        {formatMinutesSaved(t, aiSummary.estimated_minutes_saved)}
                      </p>
                    </div>
                  </div>

                  <p className="text-xs text-slate-500 mb-4">
                    {t("subscription.ai_usage.cost_hint", { rate })}
                  </p>
                  <p className="text-xs text-slate-500 mb-8">{t("subscription.ai_usage.time_hint")}</p>

                  {monthly.length > 0 && (
                    <div className="mb-8">
                      <h3 className="text-sm font-semibold text-slate-800 mb-3">
                        {t("subscription.ai_usage.monthly_overview")}
                      </h3>
                      <div className="space-y-2">
                        {monthly.map((row) => (
                          <div key={row.month} className="flex items-center gap-3 text-sm">
                            <span className="w-20 text-slate-600 tabular-nums shrink-0">{row.label}</span>
                            <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                              <div
                                className="h-full rounded-full bg-indigo-500/90"
                                style={{
                                  width: `${Math.min(
                                    100,
                                    ((row.total_tokens || 0) / maxMonthTokens) * 100
                                  )}%`,
                                }}
                              />
                            </div>
                            <span className="w-24 text-right text-slate-700 tabular-nums shrink-0">
                              {(row.total_tokens || 0).toLocaleString()}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {(aiSummary.by_feature || []).length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-slate-800 mb-3">
                        {t("subscription.ai_usage.by_feature")}
                      </h3>
                      <div className="overflow-x-auto rounded-lg border border-slate-200">
                        <table className="min-w-full text-sm">
                          <thead className="bg-slate-50 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">
                            <tr>
                              <th className="px-4 py-3">{t("subscription.ai_usage.col_feature")}</th>
                              <th className="px-4 py-3 text-right">
                                {t("subscription.ai_usage.col_calls")}
                              </th>
                              <th className="px-4 py-3 text-right">
                                {t("subscription.ai_usage.col_tokens")}
                              </th>
                              <th className="px-4 py-3 text-right">
                                {t("subscription.ai_usage.col_cost")}
                              </th>
                              <th className="px-4 py-3 text-right">
                                {t("subscription.ai_usage.col_time")}
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {aiSummary.by_feature.map((row) => (
                              <tr key={row.feature} className="hover:bg-slate-50/80">
                                <td className="px-4 py-3 text-slate-900">
                                  {featureLabel(t, row.feature)}
                                </td>
                                <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                                  {row.successful_calls}
                                </td>
                                <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                                  {(row.total_tokens || 0).toLocaleString()}
                                </td>
                                <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                                  {new Intl.NumberFormat("nb-NO", {
                                    style: "currency",
                                    currency: "NOK",
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 2,
                                  }).format(row.estimated_cost_nok ?? 0)}
                                </td>
                                <td className="px-4 py-3 text-right text-slate-700">
                                  {formatMinutesSaved(t, row.estimated_minutes_saved)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  function Content() {
    return (
      <>
        <div className="col-span-3">
          <AiUsageSection />

          {error && (
            <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-red-600"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {modules.map((module) => (
              <div
                key={module.name}
                className={`group relative bg-white rounded-xl shadow-sm overflow-hidden border transition-all duration-200 ${
                  module.active
                    ? "border-indigo-500 shadow-lg ring-2 ring-indigo-500/20"
                    : "border-gray-200 hover:border-gray-300 hover:shadow-md"
                } ${!module.enabled && !module.active ? "opacity-50" : ""}`}
              >
                {module.active && (
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 to-blue-600" />
                )}

                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-xl font-bold text-gray-900 mb-2 tracking-tight">
                        {module.title}
                      </h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{module.description}</p>
                    </div>
                    {module.active && (
                      <span className="ml-3 flex-shrink-0 inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-800 ring-1 ring-inset ring-indigo-200">
                        Aktiv
                      </span>
                    )}
                  </div>

                  <div className="mb-6">
                    <div className="flex items-baseline gap-2">
                      <p className="text-2xl font-bold text-gray-900">
                        {module.price_on_request || module.price == null ? (
                          <span>{t("trainingAdmin.priceOnRequest")}</span>
                        ) : module.price === 0 ? (
                          <span className="text-green-600">Gratis</span>
                        ) : (
                          new Intl.NumberFormat("nb-NO", {
                            style: "currency",
                            currency: "NOK",
                            minimumFractionDigits: 0,
                          }).format(module.price)
                        )}
                      </p>
                      {!module.price_on_request && module.price != null && module.price !== 0 && (
                        <span className="text-sm text-gray-500">
                          / {module.period === "month" ? "mnd" : "år"}
                        </span>
                      )}
                    </div>
                  </div>

                  {module.name === "training_guides" && (
                    <p className="mb-4 rounded-xl bg-blue-50 p-3 text-sm text-blue-900">{t("trainingAdmin.commercialActivation")}</p>
                  )}

                  {!module.enabled && (
                    <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-xs font-medium text-amber-800">
                        {module.active
                          ? "Katalog lukket — dere beholder tilgang. Bruk Deaktiver for å fjerne den, eller kontakt Promang."
                          : "Ikke tilgjengelig for ny aktivering. Kontakt Promang / admin dashboard."}
                      </p>
                    </div>
                  )}

                  <button
                    onClick={() => handleToggleClick(module)}
                    disabled={saving || (!module.active && !module.enabled)}
                    className={`w-full py-3 px-4 rounded-lg font-semibold text-sm transition-all duration-200 ${
                      module.active
                        ? "bg-red-50 text-red-700 hover:bg-red-100 border-2 border-red-300 hover:border-red-400 active:bg-red-200"
                        : "bg-gradient-to-r from-indigo-600 to-blue-600 text-white hover:from-indigo-700 hover:to-blue-700 shadow-sm hover:shadow-md active:scale-[0.98]"
                    } ${
                      saving || (!module.active && !module.enabled)
                        ? "opacity-50 cursor-not-allowed"
                        : "cursor-pointer"
                    }`}
                  >
                    {saving ? (
                      <span className="flex items-center justify-center">
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-current"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Lagrer...
                      </span>
                    ) : module.active ? (
                      "Fjern tilgang"
                    ) : (
                      "Aktiver"
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 p-5 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-blue-600 mt-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900 mb-1">Informasjon om moduler</p>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Kun aktiverte moduler vil være tilgjengelige for alle brukere, inkludert
                  admin-brukere. Moduler som ikke er aktivert vil ikke være synlige i menyen og vil
                  ikke være tilgjengelige i systemet.
                </p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (isLoading) {
    return <ResourceLoading />;
  }

  return (
    <>
      <DashboardHeader
        subtitle={`Aktiver eller deaktiver moduler for ${currentCompany?.name}`}
        actions={[]}
        title={"Mitt abonnement"}
        meta={[]}
      >
        <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
          <Content />
        </div>
      </DashboardHeader>

      <SimplePrompt
        open={promptOpen}
        onClose={() => {
          setPromptOpen(false);
          setModuleToToggle(null);
        }}
        title="Aktiver modul"
        description={`Er du sikker på at du vil aktivere "${moduleToToggle?.title}"?`}
        onAccept={handleConfirmToggle}
      />
    </>
  );
};

export default CompanySubscription;
